import React from "react";
import { CheckCircle2, User, Smartphone, ArrowRight, MapPin, Calendar } from "lucide-react";

const SuccessScreen = ({ data, onOk }) => {
  // 1. Get LocalStorage Data
  const getLocalData = () => {
    const stored = localStorage.getItem('customer_data');
    try {
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  };

  // 2. THE PICKER: If 'data' prop exists, use it. Else, use localStorage.
  const source = data || getLocalData();

  if (!source) return null;

  // 3. Expiry Date Logic (Current Date + Validity Days)
  const calculateExpiry = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + Number(days || 0));
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-all">
      <div className="bg-white rounded-[1.5rem] w-full max-w-md shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-hidden h-auto max-h-[95vh] flex flex-col">
        
        {/* Success Header */}
        <div className="text-center pt-8 pb-4 px-6 bg-gradient-to-b from-emerald-50/50 to-white">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm ring-4 ring-emerald-50">
            <CheckCircle2 size={32} strokeWidth={2.5} />
          </div>
          <h2 className="text-lg font-black text-slate-800 tracking-tight">
            Registration Successful
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Plan activated successfully
          </p>
        </div>

        {/* Details Section */}
        <div className="p-5 space-y-5 overflow-y-auto">
          
          {/* Customer Group */}
          <Section title="Customer Details" icon={<User size={12} />}>
            <InfoRow label="Full Name" value={source.fullName || source.name} />
            <InfoRow label="Mobile" value={source.mobile ? `+91 ${source.mobile}` : "—"} />
            <InfoRow label="Email" value={source.email} />
          </Section>

          {/* Location Group */}
          <Section title="Location" icon={<MapPin size={12} />}>
            <InfoRow label="District" value={source.district || source.city} />
            <InfoRow label="State" value={source.state} />
            <InfoRow label="Pincode" value={source.pincode} />
          </Section>

          {/* Plan Highlight Card */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition-all group">
            <div className="flex justify-between items-center mb-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Plan Summary
              </p>
              <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold shadow-sm">ACTIVE</span>
            </div>
            
            <div className="space-y-2">
                <InfoRow label="Package" value={source.package || "Standard Protection"} />
                <InfoRow label="Validity" value={`${source.validity_days || 0} Days`} />
                <InfoRow 
                  label="Expires On" 
                  value={calculateExpiry(source.validity_days)} 
                />
            </div>

            <div className="mt-3 pt-3 border-t border-slate-200/50 flex justify-between items-center">
                <span className="text-xs font-medium text-slate-500">Amount Paid</span>
                <span className="text-lg font-black text-emerald-600">₹{source.planPrice || source.product_price || "0"}</span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="p-5 bg-slate-50/50 border-t border-slate-100">
          <button
            onClick={onOk}
            className="group w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-emerald-600 transition-all active:scale-[0.97] flex items-center justify-center gap-2 shadow-lg shadow-slate-200"
          >
            Back to Login
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper components
const Section = ({ title, icon, children }) => (
  <div className="space-y-2.5">
    <div className="flex items-center gap-2 border-b border-slate-100 pb-1.5">
      <span className="text-slate-400 font-bold">{icon}</span>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
        {title}
      </p>
    </div>
    <div className="space-y-2 px-1">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-start text-sm group/row">
    <span className="text-slate-500 group-hover/row:text-slate-700 transition-colors">{label}</span>
    <span className="font-bold text-slate-800 text-right">{value || "—"}</span>
  </div>
);

export default SuccessScreen;